/**
 * WorkGroup — the ONE container a work list is placed in.
 *
 * ── Why one, when there were three ───────────────────────────────────────────
 * The prototype used to show three: flush under a `Section` eyebrow, wrapped in
 * a `CardList`, and the collapsible group. They were never three *containers* —
 * they were the same card with parts missing. Flush was a group with no card
 * and no count; CardList was a group that couldn't collapse. So the group is
 * the container and the other two are it with a prop turned off (Matt,
 * 2026-07-22: "we really only want to use Grouped").
 *
 * ── The header is `SectionTitle`, not a fork ─────────────────────────────────
 * The tell that these had drifted was the tally: `CardList` rendered the
 * canonical `<Count>` pill and the group hand-rolled a bare `type-subtext` span, so
 * the same number looked like two different things one pane apart. Rather than
 * copy the pill, the group now renders the real `SectionTitle variant="card"` —
 * which owns the count pill, the "View all" link treatment, and the action
 * cluster — and adds exactly two things to it: a disclosure control in the
 * title slot, and a tint.
 *
 * ── The tint ────────────────────────────────────────────────────────────────
 * `bg-foreground/[0.04]`, the same wash `CardStrip` uses for the lightbulb
 * footnote — a WASH over whatever it sits on, not a rung of the surface ladder,
 * because `surface-2` is 3/255 off the card in dark mode and can't define a
 * block. It gives the header a floor-and-ceiling relationship with the rows: the
 * strip is chrome, the rows are content, and you can tell at a glance which is
 * which (Matt, 2026-07-22: "make that title strip a bit lighter so it can be
 * differentiated from the work items").
 *
 * It ships as its own block rather than as two more props on `SectionTitle`,
 * because the disclosure state is the group's, not the title's. What it must NOT
 * become is a second title row with its own count style — that is the bug being
 * fixed here, and the reason the header is the real `SectionTitle` rather than a
 * copy of it.
 */

import * as React from "react";
import { Button } from "../button";
import { Card } from "../card";
import { cn } from "../utils";
import { IconTooltip } from "./icon-tooltip";
import { SectionTitle, SectionTitleLink } from "./section-title";
import { ChevronDown, ChevronRight, Plus } from "../icons";

export interface WorkGroupProps {
  title: React.ReactNode;
  /** The tally → the canonical `<Count>` pill, identical everywhere. */
  count?: number;
  /** Collapsible groups get the chevron; a plain card list passes `false`. */
  collapsible?: boolean;
  defaultOpen?: boolean;
  /** "See all" — the canonical `SectionTitleLink` (muted + arrow, never blue;
   *  see the note on VIEW_ALL_LINK), so every group header's link is the one
   *  treatment rather than a per-surface guess. */
  seeAllHref?: string;
  seeAllLabel?: string;
  /** The `+` affordance. Icon-only, so it carries a tooltip (rule 7). */
  onAdd?: () => void;
  /** What the `+` adds, named: "New issue", "New goal". */
  addLabel?: string;
  /**
   * A quiet note in the header's action cluster, LEFT of "See all" — the CLI
   * equivalent of the panel (`agent health`), a source, a freshness stamp.
   *
   * Deliberately narrow: it is a passthrough for TEXT that qualifies the group,
   * not a second action slot. Buttons belong on `onAdd` or on the rows, and a
   * group header that accumulates controls is how the three containers this
   * component replaced drifted apart in the first place.
   */
  aside?: React.ReactNode;
  className?: string;
  children: React.ReactNode;
}

export function WorkGroup({
  title,
  count,
  collapsible = true,
  defaultOpen,
  seeAllHref,
  seeAllLabel = "See all",
  onAdd,
  addLabel = "Add",
  aside,
  className,
  children,
}: WorkGroupProps) {
  // Default-open, EXCEPT a provably-empty group (count === 0) starts collapsed —
  // an empty section shouldn't spend a screenful of blank state, but it stays one
  // click from opening (Matt, 2026-07-23: "if we have no open issues make it
  // default closed"). A caller can still force either state with `defaultOpen`.
  const [open, setOpen] = React.useState(defaultOpen ?? count !== 0);
  const showing = collapsible ? open : true;

  // A DEDICATED chevron button, BESIDE the title rather than wrapping it. The
  // wrap-the-whole-title version couldn't coexist with a LINK title (an `<a>`
  // inside a `<button>` is invalid, so link-titled groups — a project card, a
  // section header — had to opt out of collapsing with `collapsible={false}`).
  // Parking the disclosure in its own button makes EVERY section minimizable,
  // link title or not (Matt, 2026-07-23: "we always need the dropdown on the
  // sections so we can minimize them").
  const titleNode = collapsible ? (
    <span className="inline-flex min-w-0 items-center gap-1.5">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-label={open ? "Collapse section" : "Expand section"}
        className="-ml-1 inline-flex size-5 shrink-0 items-center justify-center rounded-sm text-muted-foreground transition-colors hover:text-foreground"
      >
        {open ? (
          <ChevronDown className="size-3.5" aria-hidden />
        ) : (
          <ChevronRight className="size-3.5" aria-hidden />
        )}
      </button>
      {title}
    </span>
  ) : (
    title
  );

  const action =
    seeAllHref || onAdd || aside ? (
      <div className="flex items-center gap-2">
        {aside ? <span className="type-subtext text-muted-foreground">{aside}</span> : null}
        {seeAllHref ? <SectionTitleLink href={seeAllHref}>{seeAllLabel}</SectionTitleLink> : null}
        {onAdd ? (
          <IconTooltip label={addLabel}>
            {/* `outline`, not `ghost`: the `+` carries its own border so it reads
             *  as a button on the tinted header strip rather than a floating
             *  glyph, and `icon-sm` matches the header's other controls
             *  (Matt, 2026-07-23: "same icon sizing … it needs a border"). */}
            <Button variant="outline" size="icon-sm" aria-label={addLabel} onClick={onAdd}>
              <Plus className="size-3.5" />
            </Button>
          </IconTooltip>
        ) : null}
      </div>
    ) : null;

  return (
    <Card className={cn("gap-0 overflow-hidden py-0", className)}>
      <SectionTitle
        variant="card"
        size="sm"
        // The wash + the softer hairline: chrome, sitting above content.
        className={cn("bg-foreground/[0.04] border-border/60", !showing && "border-b-0")}
        title={titleNode}
        count={count}
        action={action}
      />
      {showing ? children : null}
    </Card>
  );
}
